'use client';
import React, { useState, useEffect, useRef } from 'react';
import { Sparkles, Search, X, Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import { useChat } from '@/lib/hooks/useChat';
import { toast } from 'sonner';
import type { Problem, Solution } from './mockData';

interface AISmartSearchProps {
  problems: Problem[];
  solutions: Solution[];
  onResults: (results: { problems: Problem[]; solutions: Solution[] } | null) => void;
  isActive: boolean;
  onToggle: () => void;
}

const EXAMPLE_QUERIES = [
  'Car wash businesses in Zimbabwe under $100,000',
  'Low-risk startups in South Africa',
  'Agriculture businesses needing investors',
  'Businesses with ROI above 30%',
  'Renewable energy projects in Africa',
];

export default function AISmartSearch({ problems, solutions, onResults, isActive, onToggle }: AISmartSearchProps) {
  const [query, setQuery] = useState('');
  const [aiAnswer, setAiAnswer] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const { response, isLoading, error, sendMessage } = useChat(
    'GEMINI',
    'gemini/gemini-2.5-flash',
    false
  );

  useEffect(() => {
    if (error) toast.error(error.message);
  }, [error]);

  useEffect(() => {
    if (response && hasSearched) {
      setAiAnswer(response);
      // Parse AI response to filter matching items
      const matched = parseAIResponseToResults(response, problems, solutions);
      onResults(matched);
    }
  }, [response, hasSearched]);

  useEffect(() => {
    if (isActive && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isActive]);

  function parseAIResponseToResults(
    aiText: string,
    allProblems: Problem[],
    allSolutions: Solution[]
  ): { problems: Problem[]; solutions: Solution[] } {
    const lower = aiText.toLowerCase();

    // Extract IDs mentioned in the AI response
    const mentionedProblemIds = allProblems.filter((p) => {
      const titleLower = p.title.toLowerCase();
      const excerptLower = p.excerpt.toLowerCase();
      return lower.includes(titleLower.slice(0, 20)) || lower.includes(p.id);
    });

    const mentionedSolutionIds = allSolutions.filter((s) => {
      const titleLower = s.title.toLowerCase();
      return lower.includes(titleLower.slice(0, 20)) || lower.includes(s.id);
    });

    // If AI didn't mention specific items, do keyword-based fallback from the query
    if (mentionedProblemIds.length === 0 && mentionedSolutionIds.length === 0) {
      // Extract key terms from AI response for secondary matching
      const words = lower.split(/\s+/).filter((w) => w.length > 4);
      const filteredProblems = allProblems.filter((p) =>
        words.some(
          (w) =>
            p.title.toLowerCase().includes(w) ||
            p.excerpt.toLowerCase().includes(w) ||
            p.category.toLowerCase().includes(w) ||
            (p.country || '').toLowerCase().includes(w)
        )
      );
      const filteredSolutions = allSolutions.filter((s) =>
        words.some(
          (w) =>
            s.title.toLowerCase().includes(w) ||
            s.excerpt.toLowerCase().includes(w) ||
            s.tags?.some((t) => t.toLowerCase().includes(w))
        )
      );
      return { problems: filteredProblems, solutions: filteredSolutions };
    }

    return { problems: mentionedProblemIds, solutions: mentionedSolutionIds };
  }

  function buildSystemPrompt(): string {
    const problemSummaries = problems
      .slice(0, 50)
      .map(
        (p) =>
          `ID:${p.id} | Title:"${p.title}" | Category:${p.category} | Country:${p.country || 'N/A'} | Funding:$${p.fundingTarget} | Status:${p.status} | Excerpt:"${p.excerpt?.slice(0, 80)}"`
      )
      .join('\n');

    const solutionSummaries = solutions
      .slice(0, 30)
      .map(
        (s) =>
          `ID:${s.id} | Title:"${s.title}" | FundingAsk:$${s.fundingAsk} | Tags:${s.tags?.join(',')} | Excerpt:"${s.excerpt?.slice(0, 80)}"`
      )
      .join('\n');

    return `You are an AI search assistant for Monezite, an African business opportunity and problem-solution platform.

Your job is to understand natural language queries and find the most relevant problems and business solutions from the database below.

PROBLEMS DATABASE:
${problemSummaries || 'No problems available yet.'}

SOLUTIONS/BUSINESSES DATABASE:
${solutionSummaries || 'No solutions available yet.'}

When a user searches, analyze their query and:
1. Identify relevant items by title keywords, category, country, funding range, or description
2. Briefly explain what you found (2-3 sentences max)
3. List the matching item titles clearly

Be concise and helpful. Focus on African business context.`;
  }

  async function handleSearch(e?: React.FormEvent) {
    if (e) e.preventDefault();
    if (!query.trim() || isLoading) return;

    setHasSearched(true);
    setAiAnswer('');
    onResults(null);

    sendMessage(
      [
        { role: 'system', content: buildSystemPrompt() },
        { role: 'user', content: query },
      ],
      { temperature: 0.3, max_tokens: 500 }
    );
  }

  function handleClear() {
    setQuery('');
    setAiAnswer('');
    setHasSearched(false);
    onResults(null);
  }

  function handleExampleClick(example: string) {
    setQuery(example);
    setTimeout(() => handleSearch(), 50);
  }

  return (
    <div className="border-b border-border bg-gradient-to-r from-primary/5 to-transparent">
      {/* Header toggle */}
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-muted/30 transition-colors"
      >
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-md bg-gradient-to-br from-primary to-purple-600 flex items-center justify-center">
            <Sparkles size={12} className="text-white" />
          </div>
          <span className="text-sm font-bold text-foreground">AI Smart Search</span>
          <span className="text-xs font-medium text-primary bg-primary/10 px-1.5 py-0.5 rounded-full">
            Gemini
          </span>
        </div>
        {isActive ? (
          <ChevronUp size={14} className="text-muted-foreground" />
        ) : (
          <ChevronDown size={14} className="text-muted-foreground" />
        )}
      </button>

      {isActive && (
        <div className="px-4 pb-4 space-y-3">
          {/* Search input */}
          <form onSubmit={handleSearch} className="relative">
            <div className="relative flex items-center">
              <Sparkles size={14} className="absolute left-3 text-primary pointer-events-none" />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder='Try: "Low-risk AgriTech in Zimbabwe under $50k"'
                className="w-full pl-9 pr-20 py-2.5 rounded-xl border border-primary/30 bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-all"
                disabled={isLoading}
              />
              <div className="absolute right-2 flex items-center gap-1">
                {(query || hasSearched) && (
                  <button
                    type="button"
                    onClick={handleClear}
                    className="p-1 rounded-lg hover:bg-muted transition-colors"
                  >
                    <X size={12} className="text-muted-foreground" />
                  </button>
                )}
                <button
                  type="submit"
                  disabled={!query.trim() || isLoading}
                  className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-semibold disabled:opacity-50 hover:bg-primary/90 transition-colors"
                >
                  {isLoading ? (
                    <Loader2 size={11} className="animate-spin" />
                  ) : (
                    <Search size={11} />
                  )}
                  {isLoading ? 'Searching...' : 'Search'}
                </button>
              </div>
            </div>
          </form>

          {/* Example queries */}
          {!hasSearched && (
            <div className="flex flex-wrap gap-1.5">
              {EXAMPLE_QUERIES.map((ex) => (
                <button
                  key={ex}
                  type="button"
                  onClick={() => handleExampleClick(ex)}
                  disabled={isLoading}
                  className="text-xs px-2.5 py-1 rounded-full border border-border bg-muted/50 text-muted-foreground hover:border-primary hover:text-primary hover:bg-primary/5 transition-colors disabled:opacity-50"
                >
                  {ex}
                </button>
              ))}
            </div>
          )}

          {/* AI Answer */}
          {isLoading && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground py-1">
              <Loader2 size={12} className="animate-spin text-primary" />
              <span>Gemini is analyzing your query...</span>
            </div>
          )}

          {aiAnswer && !isLoading && (
            <div className="bg-primary/5 border border-primary/20 rounded-xl p-3">
              <div className="flex items-center gap-1.5 mb-1.5">
                <Sparkles size={11} className="text-primary" />
                <span className="text-xs font-semibold text-primary">AI Insight</span>
              </div>
              <p className="text-xs text-foreground leading-relaxed">{aiAnswer}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
